import styled from '@emotion/styled';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ToastManager } from '../../../ToastManager';

import { AppUtils } from '../../../store/apps/AppUtils';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApp } from '../../../store/apps/useApps.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';

import { animationFade } from '../../../theme/animations/animationFade';
import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { GradientButton } from '../../../components/buttons/GradientButton';
import { IconButton } from '../../../components/buttons/IconButton';
import { Clipboard } from '../../../components/common/Clipboard';
import { HomeyIcon } from '../../../components/common/HomeyIcon';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { SpinnerFade } from '../../../components/common/SpinnerFade';
import { Heading3Medium } from '../../../components/common/text/Heading3Medium';
import { ParagraphRegular } from '../../../components/common/text/ParagraphRegular';
import { TextTinyMedium } from '../../../components/common/text/TextTinyMedium';
import { TextareaField } from '../../../components/forms/TextareaField';
import { DialogShiftLock } from '../../../components/overlay/DialogShiftLock';

export function AppSettingsDialogDiagnosticsReport(props) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const { app } = useApp({ appId: props.appId });
  const formId = 'app-diagnostics-report-form';

  const appBackgroundColor = AppUtils.getBackgroundColor(app);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      message: '',
    },
    mode: 'onSubmit',
  });

  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);

  function handleClosePress() {
    props.onCloseRequest();
  }

  function handleFormSubmit(data) {
    setIsSending(true);
    const minDuration = new Promise((resolve) => setTimeout(resolve, 2000));

    api.apps
      .getAppStd({
        id: props.appId,
        message: data.message,
      })
      .then(async (result) => {
        await minDuration;
        setResult(result);
      })
      .catch(ToastManager.handleError)
      .finally(() => {
        setIsSending(false);
      });
  }

  return (
    <DialogShiftLock layoutId={result != null ? '2' : '1'} onClose={props.onCloseRequest}>
      <S.Root>
        <S.CloseIconButtonWrapper>
          <IconButton
            url={glyph.core.cross}
            color={theme.color.icon_light}
            size={theme.icon.size_default}
            onPress={handleClosePress}
          />
        </S.CloseIconButtonWrapper>

        <RoundIconWrapper size="80px" color={appBackgroundColor}>
          <HomeyIcon size="32px" iconObj={app?.iconObj} color={theme.color.white} />
        </RoundIconWrapper>

        <S.Title>{i18n.messageFormatter('settings.app.diagnosticsReport')}</S.Title>

        <S.ViewWrapper id={formId} onSubmit={handleSubmit(handleFormSubmit)}>
          {(() => {
            if (result != null) return;

            const minLength = 10;
            return (
              <S.MessageView>
                <ParagraphRegular textAlign="center" color={theme.color.text_light}>
                  {i18n.messageFormatter('settings.app.diagnosticsReportHint')}
                </ParagraphRegular>

                <TextareaField
                  register={register}
                  aria-label="Message"
                  name="message"
                  rows={4}
                  orientation="vertical"
                  isDisabled={isSending}
                  minLength={minLength}
                  required={true}
                  placeholder={i18n.messageFormatter('settings.app.diagnosticsReportPlaceholder')}
                  error={errors.message}
                >
                  {errors.message && (
                    <S.FormError color={theme.color.error}>
                      {
                        {
                          minLength: i18n.messageFormatter('common.minLength', { minLength }),
                          required: i18n.messageFormatter('common.required'),
                        }[errors.message.type]
                      }
                    </S.FormError>
                  )}
                </TextareaField>
              </S.MessageView>
            );
          })()}

          {result != null && (
            <S.CodeView>
              <ParagraphRegular textAlign="center" color={theme.color.text_light}>
                {i18n.messageFormatter('settings.app.diagnosticsReportCodeHint')}
              </ParagraphRegular>

              <Clipboard value={result} />
            </S.CodeView>
          )}
        </S.ViewWrapper>

        <S.Actions>
          <GradientButton
            form={result == null ? formId : undefined}
            type={result == null ? 'submit' : 'button'}
            isDisabled={isSending === true}
            styleFlat={true}
            styleColor="blue"
            onPress={result != null ? handleClosePress : undefined}
          >
            <SpinnerFade
              isActive={isSending}
              size={theme.icon.size_small}
              color={theme.color.white}
            >
              {result == null && (
                <GradientButton.Icon
                  source={glyph.core.paperAirplane}
                  size={theme.icon.size_default}
                  color={theme.color.white}
                />
              )}
            </SpinnerFade>
            <GradientButton.Text>
              {isSending
                ? `${i18n.messageFormatter('common.sending')}...`
                : result == null
                  ? i18n.messageFormatter('common.send')
                  : i18n.messageFormatter('common.done')}
            </GradientButton.Text>
          </GradientButton>
        </S.Actions>
      </S.Root>
    </DialogShiftLock>
  );
}

function S() {}
AppSettingsDialogDiagnosticsReport.S = S;

S.Root = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  width: 360px;
  padding: ${su(4)} ${su(2)} ${su(2)};

  ${TextareaField.Root} {
    width: 100%;
  }
`;

S.CloseIconButtonWrapper = styled.div`
  position: absolute;
  right: ${su(2)};
  top: ${su(2)};
`;

S.ViewWrapper = styled.form`
  width: 100%;
`;

S.MessageView = styled.div``;

S.CodeView = styled.div`
  opacity: 0;
  animation: ${animationFade.in} ${motion.duration.normal} ease-out forwards;
`;

S.Title = styled(Heading3Medium)`
  padding-top: ${su(2)};
`;

S.Actions = styled.div`
  display: grid;
  width: 100%;
  grid-auto-flow: column;
  grid-gap: ${su(1)};
  padding-top: ${su(2)};
`;

S.FormError = styled(TextTinyMedium)`
  position: absolute;
  right: 4px;
  bottom: -16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;
